import React from 'react'

const BOLD_PATTERN = /\*\*(.+?)\*\*/g

/**
 * Splits text by **bold** markers and returns an array of string | ReactNode
 * so that bold segments are wrapped in <strong>.
 */
function parseBoldSegments(text: string): (string | React.ReactNode)[] {
  const parts: (string | React.ReactNode)[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  // Reset state for global regex
  BOLD_PATTERN.lastIndex = 0
  while ((match = BOLD_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    parts.push(<strong key={`b-${match.index}`}>{match[1]}</strong>)
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }
  return parts.length ? parts : [text]
}

export interface AiTextRenderProps {
  /** AI-generated text that may contain * bullets and **bold** */
  text: string
  /** Optional class name for the wrapper */
  className?: string
  /** Tag for the root wrapper when there are no list items: 'p' | 'div' (default 'div') */
  as?: 'p' | 'div'
}

/**
 * Renders AI-style text with simple markdown-like effects:
 * - Lines starting with "* " are shown as bullet list items
 * - **text** is rendered as bold
 *
 * Use this for AI engine output (e.g. chat history, summaries).
 */
export function AiTextRender({ text, className = '', as: Root = 'div' }: AiTextRenderProps) {
  if (!text || typeof text !== 'string') {
    return <Root className={className} />
  }

  const lines = text.split(/\r?\n/)
  const hasBullets = lines.some((line) => /^\s*\*\s+/.test(line))

  if (!hasBullets) {
    return (
      <Root className={className}>
        {lines.map((line, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {parseBoldSegments(line)}
          </React.Fragment>
        ))}
      </Root>
    )
  }

  const blocks: React.ReactNode[] = []
  let listItems: React.ReactNode[] = []
  let key = 0

  const flushList = () => {
    if (listItems.length > 0) {
      blocks.push(
        <ul key={key++} className="list-disc list-inside space-y-0.5 ml-1">
          {listItems}
        </ul>
      )
      listItems = []
    }
  }

  lines.forEach((line) => {
    const bulletMatch = line.match(/^\s*\*\s+(.*)$/)
    if (bulletMatch) {
      listItems.push(
        <li key={listItems.length}>{parseBoldSegments(bulletMatch[1])}</li>
      )
    } else {
      flushList()
      if (line.trim()) {
        blocks.push(
          <span key={key++} className="block my-0.5">
            {parseBoldSegments(line)}
          </span>
        )
      }
    }
  })
  flushList()

  return <Root className={className}>{blocks}</Root>
}

/**
 * Returns React nodes for the given AI text (for use inside custom wrappers).
 * - **text** → bold
 * - Lines starting with "* " are rendered as <li>; you wrap with <ul> if needed.
 */
export function renderAiTextFragments(text: string): React.ReactNode {
  if (!text || typeof text !== 'string') return null
  return <AiTextRender text={text} />
}
